import React from "react";
import { V2_REDESIGN_FORCE_DATE } from "@gettactic/api";
import {
  SettingContext,
  SettingsState,
  useAuthenticated,
  useIsPrivacyEnabled,
  useOrganizationSettings,
  useTacticHooks
} from "@gettactic/hooks";
import { useBooleanState } from "@gettactic/helpers";
import Confetti from "react-confetti-boom";

export const SettingsProvider: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const organizationId = authenticatedUser?.organization?.id;
  const { data: organizationSettings, isLoading: isOrganizationSettingsLoading, refetch } = useOrganizationSettings({
    id: organizationId,
    refetchInterval: 1000 * 60
  });

  const isOrgPrivacyEnabled = useIsPrivacyEnabled({isOrgPrivacyEnabled: organizationSettings?.enable_privacy_mode ?? false});
  const { apiClient } = useTacticHooks();
  const isLoading = useBooleanState();
  const organizationIsAfterForceDate = authenticatedUser?.organization?.created
    ? new Date(authenticatedUser.organization.created) > V2_REDESIGN_FORCE_DATE
    : false;

  const defaultDesignSystemVersion = organizationIsAfterForceDate ? "v2" : "v1";

  const switchDesignSystem = async (version: "v1" | "v2") => {
    if (version === "v1" || version === "v2") {
      isLoading.true();
      const settingsRaw =
        await apiClient.orgOrOfficeSettings.getOrganizationSettings();
      const settings = settingsRaw.result;

      const updatedSettings = {
        ...settings,
        design_system_version: version
      };

      try {
        await apiClient.orgOrOfficeSettings.updateOrganizationSettings(
          updatedSettings
        );
        await refetch();
      } catch (error) {
        console.error("Error updating theme settings:", error);
      } finally {
        isLoading.false();
      }
    } else {
      throw new Error("Invalid design system version");
    }
  };

  const isConfettiEnabled = useBooleanState();

  const throwConfetti = () => {
    isConfettiEnabled.true();
    setTimeout(() => {
      isConfettiEnabled.false();
    }, 5000);
  };

  const state: SettingsState = {
    designSystem: organizationIsAfterForceDate
      ? "v2"
      : organizationSettings?.design_system_version ||
        defaultDesignSystemVersion,
    organizationIsAfterForceDate,
    isLoading: isLoading.state,
    isOrganizationSettingsLoading,
    switchDesignSystem,
    haveConfetti: isConfettiEnabled.state,
    throwConfetti,

    isOrgPrivacyEnabled: isOrgPrivacyEnabled
  };

  return (
    <SettingContext.Provider value={state}>
      {children}
      {isConfettiEnabled.state && <Confetti mode="boom" particleCount={600} />}
    </SettingContext.Provider>
  );
};
